/**
 * @contract
 *   Назначение: Общий хук отправки отзыва о ЧОПе через POST /api/seo/review
 *   Используется: chop-review-form.tsx (inline) + add-review-dialog.tsx (dialog)
 *   Поля:        chop_id, author_name, author_role, rating, content, honeypot
 *   Состояния:   idle → submitting → success | error
 *   Валидация:   rating > 0, content.trim().length >= 20, author_name — на усмотрение компонента
 *   Автор:       AI Agent (рефакторинг дублирования)
 *   Дата:        2026-06-23
 */

"use client";

import { useState, useCallback } from "react";

export type SubmitState = "idle" | "submitting" | "success" | "error";

export interface ReviewPayload {
  chop_id: string;
  author_name: string;
  author_role: string;
  rating: number;
  content: string;
  honeypot?: string;
}

interface UseReviewSubmitOptions {
  /** Минимальная длина текста отзыва (по умолчанию 20) */
  minContentLength?: number;
  /** Дополнительная валидация перед отправкой. Возвращает сообщение об ошибке или null. */
  customValidate?: (payload: ReviewPayload) => string | null;
}

interface UseReviewSubmitReturn {
  state: SubmitState;
  errorMessage: string;
  /** Отправить отзыв. Возвращает true при успехе. */
  submit: (payload: ReviewPayload) => Promise<boolean>;
  /** Сбросить состояние в idle */
  reset: () => void;
}

/**
 * Хук для отправки отзыва о ЧОПе.
 *
 * Инкапсулирует fetch-логику, обработку состояний и базовую валидацию.
 * Оба UI-компонента (инлайн-форма и диалог) используют этот хук,
 * что исключает дрифт валидации и API-контракта.
 */
export function useReviewSubmit(
  options: UseReviewSubmitOptions = {}
): UseReviewSubmitReturn {
  const { minContentLength = 20, customValidate } = options;

  const [state, setState] = useState<SubmitState>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const submit = useCallback(
    async (payload: ReviewPayload): Promise<boolean> => {
      // Базовая валидация
      if (payload.rating === 0) {
        setErrorMessage("Пожалуйста, поставьте оценку.");
        setState("error");
        return false;
      }
      if (payload.content.trim().length < minContentLength) {
        setErrorMessage(
          `Текст отзыва должен содержать минимум ${minContentLength} символов.`
        );
        setState("error");
        return false;
      }

      // Кастомная валидация (например, обязательное имя в dialog-версии)
      if (customValidate) {
        const customError = customValidate(payload);
        if (customError) {
          setErrorMessage(customError);
          setState("error");
          return false;
        }
      }

      setState("submitting");
      setErrorMessage("");

      try {
        const res = await fetch("/api/seo/review", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            chop_id: payload.chop_id,
            author_name: payload.author_name.trim() || "Аноним",
            author_role: payload.author_role,
            rating: payload.rating,
            content: payload.content.trim(),
            honeypot: payload.honeypot || "",
          }),
        });

        if (res.ok || res.status === 201) {
          setState("success");
          return true;
        }

        const data = await res.json().catch(() => null);
        setErrorMessage(
          data?.error ?? "Ошибка при отправке. Попробуйте позже."
        );
        setState("error");
        return false;
      } catch {
        setErrorMessage("Ошибка сети. Проверьте подключение и попробуйте снова.");
        setState("error");
        return false;
      }
    },
    [minContentLength, customValidate]
  );

  const reset = useCallback(() => {
    setState("idle");
    setErrorMessage("");
  }, []);

  return { state, errorMessage, submit, reset };
}
